/*
 * FICHIER: useMatomo.js
 * BUT: Hooks React pour intégrer le tracking analytics avec Matomo
 * 
 * Matomo est un outil d'analyse web (comme Google Analytics) qui permet de :
 * - Tracker les pages visitées
 * - Mesurer le temps passé sur chaque page
 * - Enregistrer les erreurs JavaScript
 * - Suivre des événements spécifiques (clics, téléchargements, etc.)
 * 
 * Ce fichier contient plusieurs hooks :
 * - useMatomo: Tracking automatique des pages + méthodes manuelles
 * - usePageTiming: Mesure le temps passé sur une page/composant
 * - useErrorTracking: Capture automatique des erreurs JavaScript
 */

// Import des hooks React de base
import { useEffect, useRef } from 'react';

// Import du hook de React Router pour détecter les changements de page
import { useLocation } from 'react-router-dom';

// Import du service Matomo qui contient la logique métier
import matomoService from '../services/matomoService';

/**
 * Hook React principal pour intégrer facilement Matomo
 * 
 * UTILISATION :
 * const { trackEvent, trackGoal, trackError } = useMatomo();
 * 
 * Ce hook fait automatiquement :
 * - Le tracking des changements de page
 * - La mesure du temps passé sur chaque page
 * - La réinitialisation des timers
 * 
 * Et expose des méthodes pour le tracking manuel
 */
export const useMatomo = () => {
  // useLocation donne accès à l'URL actuelle et détecte les changements
  const location = useLocation();
  
  // useRef permet de garder une valeur qui persiste entre les rendus
  // sans déclencher de nouveaux rendus quand elle change
  const pageStartTime = useRef(Date.now()); // Timestamp du début de visite de la page

  // TRACKER AUTOMATIQUEMENT LES CHANGEMENTS DE PAGE
  useEffect(() => {
    // Vérifier si Matomo est activé (pour éviter les erreurs si mal configuré)
    if (matomoService.isActive()) {
      
      // CALCULER LE TEMPS PASSÉ SUR LA PAGE PRÉCÉDENTE
      const timeOnPage = Date.now() - pageStartTime.current;
      
      // Ne tracker que si l'utilisateur est resté plus d'1 seconde (évite les passages rapides)
      if (timeOnPage > 1000) {
        matomoService.trackTiming('Navigation', 'Temps sur page', timeOnPage);
      }

      // TRACKER LA NOUVELLE PAGE
      // trackPageView() enregistre dans Matomo que l'utilisateur a visité cette page
      matomoService.trackPageView();
      
      // RÉINITIALISER LE TIMER pour la nouvelle page
      pageStartTime.current = Date.now();
    }
  }, [location.pathname]); // Se déclenche à chaque changement d'URL

  // MÉTHODES EXPOSÉES POUR USAGE MANUEL
  // Le hook retourne des fonctions que les composants peuvent utiliser
  // .bind(matomoService) assure que 'this' pointe vers matomoService dans les méthodes
  return {
    trackEvent: matomoService.trackEvent.bind(matomoService),           // Tracker un événement (ex: clic bouton)
    trackGoal: matomoService.trackGoal.bind(matomoService),             // Tracker un objectif (ex: inscription)
    trackError: matomoService.trackError.bind(matomoService),           // Tracker une erreur manuellement
    setCustomVariable: matomoService.setCustomVariable.bind(matomoService), // Définir une variable personnalisée
    isActive: matomoService.isActive.bind(matomoService),               // Vérifier si Matomo est actif
    getAdminUrl: matomoService.getAdminUrl.bind(matomoService)          // Obtenir l'URL d'admin Matomo
  };
};

/**
 * Hook pour tracker le temps passé sur une page/composant spécifique
 * 
 * UTILISATION :
 * usePageTiming('Page Contact'); // Dans le composant ContactPage
 * 
 * Ce hook mesure automatiquement le temps entre le montage et le démontage du composant
 * 
 * @param {string} pageName - Nom de la page pour identifier dans Matomo
 */
export const usePageTiming = (pageName) => {
  // Enregistre le moment où le composant se monte
  const startTime = useRef(Date.now());

  useEffect(() => {
    // La fonction return dans useEffect est appelée quand le composant se démonte
    // C'est le "cleanup" ou "nettoyage"
    return () => {
      // Calculer le temps total passé
      const timeSpent = Date.now() - startTime.current;
      
      // Tracker seulement si plus d'1 seconde ET Matomo actif
      if (timeSpent > 1000 && matomoService.isActive()) {
        matomoService.trackTiming('Page Timing', pageName, timeSpent);
      }
    };
  }, [pageName]); // Se relance si pageName change
};

/**
 * Hook pour tracker les erreurs JavaScript automatiquement
 * 
 * UTILISATION :
 * useErrorTracking(); // Dans App.js ou un composant racine
 * 
 * Ce hook écoute automatiquement :
 * - Les erreurs JavaScript globales (window.error)
 * - Les promesses rejetées non gérées (unhandledrejection)
 * 
 * Et les envoie à Matomo pour analyse des bugs en production
 */
export const useErrorTracking = () => {
  useEffect(() => {
    // GESTIONNAIRE D'ERREURS JAVASCRIPT GLOBALES
    const handleError = (event) => {
      if (matomoService.isActive()) {
        // Envoie l'erreur à Matomo avec des informations de contexte
        matomoService.trackError(event.error, {
          filename: event.filename,   // Fichier où l'erreur s'est produite
          lineno: event.lineno,       // Numéro de ligne
          colno: event.colno          // Numéro de colonne
        });
      }
    };

    // GESTIONNAIRE DE PROMESSES REJETÉES NON GÉRÉES
    const handleUnhandledRejection = (event) => {
      if (matomoService.isActive()) {
        // Convertit le rejet de promesse en Error et l'envoie à Matomo
        matomoService.trackError(new Error(event.reason), {
          type: 'Unhandled Promise Rejection'
        });
      }
    };

    // ÉCOUTER LES ERREURS GLOBALES
    // Ces event listeners écoutent sur l'objet window (global)
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // FONCTION DE NETTOYAGE
    // Quand le composant se démonte, on retire les event listeners
    // pour éviter les fuites mémoire
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []); // Tableau vide = ne se relance jamais, se monte une seule fois
};

// Export par défaut du hook principal
export default useMatomo; 